/// <reference path="../globals.d.ts" />

import camelcaseKeys from 'camelcase-keys'

import User, { UserState } from '../data/user'
import ApiClient from './api_client'
import Cookies from './cookies'
import { setupTrackingID } from '@src/util/functions'
import { API } from '@src/lib/api_path'
import Club from '@src/data/club'
import { AllowedRoles } from '@src/router'

type LoginResponse = { session: { auth_token: string, confirmed: boolean, trainer: boolean , club_role_type: AllowedRoles} }
type ProfileResponse = { profile: User }

class SessionManager {
  private static _instance

  // trainer boolean should be removed as it no longer servers a function
  // @ts-ignore
  private trainer: boolean = false
  private clubRoleType: AllowedRoles = undefined
  private masterClub: boolean = false
  private readonly authCookieName: string = ''

  private constructor () {
    // NODE_ENV is replaced at build time, the specs always run with 'test'
    this.authCookieName = NODE_ENV === 'production' ? /* istanbul ignore next */ 'Auth-Token' : 'Staging-Auth-Token'
  }

  static get Instance (): SessionManager {
    return this._instance || (this._instance = new this())
  }

  getAuthToken () {
    return Cookies.get(this.authCookieName)
  }

  isManager () {
    return (['trainer', 'admin', 'therapist', 'assistant'] as AllowedRoles[]).includes(this.clubRoleType)
  }

  isTrainer () {
    return this.clubRoleType === 'trainer'
  }

  getRole () {
    return this.clubRoleType as AllowedRoles
  }

  isAssistant () {
    return this.clubRoleType === 'assistant'
  }

  isOwner () {
    return this.clubRoleType === 'admin'
  }

  notMemberOrAssistant () {
    return this.clubRoleType !== 'member' && this.clubRoleType !== 'assistant'
  }

  setTrainer (trainer: boolean) {
    if (trainer) {
      this.clubRoleType = 'admin'
    } else {
      this.clubRoleType = 'member'
    }
    this.trainer = trainer
  }

  isMasterClub () {
    return this.masterClub
  }

  restoreUser (): Promise<User> {
    if (!this.getAuthToken()) {
      return Promise.reject(new Error('No Cookie set'))
    }
    return this.checkSession()
  }

  loginWithPassword (login: string, password: string): Promise<User> {
    return this.login('sign_in', { login, password, type: 'password' })
  }

  loginWithToken (token: string): Promise<User> {
    return this.login('/workout_results/auth', { confirmation_token: token })
  }

  login (path: string, data): Promise<User> {
    const client = new ApiClient(this.getBaseUrl(), { skipAuth: true })
    return client
      .post<LoginResponse>(path, data)
      .then((response: LoginResponse) => {
        Cookies.set(this.authCookieName, response.session.auth_token, { expires: '5Y' })
        this.trainer = response.session.trainer
        this.clubRoleType = response.session.club_role_type
        this.setMasterClub()
        setupTrackingID(response.session.trainer)
        return this.loadProfile()
      })
  }
  setMasterClub () {
    type ClubResponse = { club: Club }
    if (this.isManager()) {
      const apiClient: ApiClient = new ApiClient(API.TRAINERS_API.CLUB)
      return apiClient.get<ClubResponse>()
        .then((response) => {
          let club = new Club(camelcaseKeys(response.club))
          this.masterClub = club.masterClub
        })
    }
  }

  logout () {
    Cookies.delete(this.authCookieName)
    Cookies.delete('Role')
    sessionStorage.clear()
  }

  getBaseUrl () {
    return this.isManager() ? 'trainers/api/v1' : 'members/api/v1'
  }

  getBaseRoute () {
    return this.isManager() ? '/trainers' : '/members'
  }

  private checkSession (): Promise<User> {
    const client = new ApiClient(this.getBaseUrl())
    return client
      .get<LoginResponse>('sign_in/check')
      .then(async (response: LoginResponse) => {
        this.trainer = response.session.trainer
        this.clubRoleType = response.session.club_role_type
        let [user] = await Promise.all([this.loadProfile(), this.setMasterClub()])
        if (user.state === UserState.DEACTIVATED) {
          throw(new Error('User is deactivated'))
        }
        setupTrackingID(response.session.trainer)
        return user
      })
      .catch((err) => {
        this.logout()
        return Promise.reject(new Error(err))
      })
  }

  private loadProfile (): Promise<User> {
    return new ApiClient(this.getBaseUrl()).get<ProfileResponse>('profile')
      .then(response => {
        let user = new User(camelcaseKeys(response.profile, { deep: true }))
        Cookies.set('Locale', user.locale, { expires: '5Y' })
        return user
      })
      .catch((err) => {
        this.logout()
        return Promise.reject(err)
      })
  }

}

export default SessionManager
